import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
} from '@mui/material';
import { FormHandles } from '@unform/core';
import { Form } from '../../shared/forms';
import { useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { VTextField } from '../../shared/forms';
import { Button } from '../../shared/components/MUI/button/Button';
import { authService, feedback } from '../../shared/services';

const resetSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('Nova senha é obrigatória')
    .min(5, 'A senha deve ter pelo menos 5 caracteres'),
  passwordConfirmation: Yup.string()
    .required('Confirmação é obrigatória')
    .oneOf([Yup.ref('newPassword')], 'As senhas não conferem'),
});

export const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (data: { newPassword: string; passwordConfirmation: string }) => {
      try {
        setErrorMessage(undefined);
        formRef.current?.setErrors({});

        await resetSchema.validate(data, { abortEarly: false });

        if (!token) {
          setErrorMessage('Token de recuperação inválido.');
          return;
        }

        setLoading(true);
        await authService.resetPassword({
          token,
          newPassword: data.newPassword,
          passwordConfirmation: data.passwordConfirmation,
        });

        feedback('Senha alterada com sucesso!', 'success');
        setSuccess(true);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors: Record<string, string> = {};
          err.inner.forEach((e) => {
            if (e.path) errors[e.path] = e.message;
          });
          formRef.current?.setErrors(errors);
        } else {
          setErrorMessage(
            err instanceof Error ? err.message : 'Erro ao redefinir senha.'
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const handleClickSend = useCallback(() => {
    formRef.current?.submitForm();
  }, []);

  return (
    <Box
      width='100vw'
      height='100vh'
      display='flex'
      alignItems='center'
      justifyContent='center'
    >
      <Card>
        <Box padding={2} width={400}>
          <CardContent>
            <Typography variant='h5' align='center'>
              Redefinir senha
            </Typography>

            <Divider variant='middle' sx={{ my: 2 }} />

            {success ? (
              <Box textAlign='center'>
                <Typography variant='body1' sx={{ mb: 2 }}>
                  Sua senha foi alterada com sucesso!
                </Typography>
                <Button
                  label='Ir para o login'
                  variant='contained'
                  onClick={() => navigate('/')}
                />
              </Box>
            ) : (
              <Form ref={formRef} onSubmit={handleSubmit}>
                <Box display='flex' flexDirection='column' gap={2}>
                  {errorMessage && (
                    <Alert severity='error'>{errorMessage}</Alert>
                  )}

                  {!token && (
                    <Alert severity='error'>
                      Token de recuperação inválido ou ausente.
                    </Alert>
                  )}

                  <VTextField
                    fullWidth
                    label='Nova senha'
                    name='newPassword'
                    type={showPassword ? 'text' : 'password'}
                    required
                    disabled={loading || !token}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            tabIndex={-1}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <VTextField
                    fullWidth
                    label='Confirmar senha'
                    name='passwordConfirmation'
                    type={showPassword ? 'text' : 'password'}
                    required
                    disabled={loading || !token}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            tabIndex={-1}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    label='Redefinir senha'
                    variant='contained'
                    disabled={loading || !token}
                    isLoading={loading}
                    onClick={handleClickSend}
                  />

                  <Button
                    label='Voltar ao login'
                    variant='text'
                    onClick={() => navigate('/')}
                    disabled={loading}
                  />
                </Box>
              </Form>
            )}
          </CardContent>
        </Box>
      </Card>
    </Box>
  );
};
